<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { SelectOption } from 'naive-ui';
import {
  getAvailableLlmProviders,
  type LlmProvider,
  type LlmProviderSpec,
  type OrganisationModelSettingsCreate,
} from '@/api';
import { logger } from '@/utils/logger';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';

const settings = defineModel<OrganisationModelSettingsCreate>({ required: true });

const availableProviders = ref<Partial<Record<LlmProvider, LlmProviderSpec>>>({});
const loadingProviders = ref<boolean>(false);

function providerLabel(provider: string, providerSpec: LlmProviderSpec | undefined): string {
  if (!providerSpec) return provider;
  const displayNames = Array.from(
    new Set(
      Object.values(providerSpec.llm_models)
        .map(modelSpec => modelSpec.display_name_provider)
        .filter(displayName => displayName.length > 0)
    )
  );
  return displayNames.length > 0 ? displayNames.join(' / ') : provider;
}

const providerOptions = computed((): SelectOption[] =>
  Object.entries(availableProviders.value).map(([provider, providerSpec]) => ({
    label: providerLabel(provider, providerSpec),
    value: provider,
  }))
);

function modelOptionsFor(provider: LlmProvider | null | undefined): SelectOption[] {
  if (!provider) return [];
  const spec: LlmProviderSpec | undefined = availableProviders.value[provider];
  const models = spec?.llm_models;
  if (!models) return [];
  return Object.entries(models).map(([modelName, modelSpec]) => ({
    label: modelSpec.display_name_model || modelName,
    value: modelName,
  }));
}

const simpleModelOptions = computed(() => modelOptionsFor(settings.value.simple_task_provider));
const complexModelOptions = computed(() => modelOptionsFor(settings.value.complex_task_provider));

function onSimpleProviderChange(value: LlmProvider | null): void {
  settings.value = {
    ...settings.value,
    simple_task_provider: value ?? undefined,
    simple_task_model: undefined,
  };
}

function onComplexProviderChange(value: LlmProvider | null): void {
  settings.value = {
    ...settings.value,
    complex_task_provider: value ?? undefined,
    complex_task_model: undefined,
  };
}

function onSimpleModelChange(value: string | null): void {
  settings.value = { ...settings.value, simple_task_model: value ?? undefined };
}

function onComplexModelChange(value: string | null): void {
  settings.value = { ...settings.value, complex_task_model: value ?? undefined };
}

onMounted(async () => {
  loadingProviders.value = true;
  try {
    const response = await getAvailableLlmProviders();
    if (response.data) {
      availableProviders.value = response.data;
    }
  } catch (error) {
    logger.error('Error loading LLM providers:', error);
  } finally {
    loadingProviders.value = false;
  }
});
</script>

<template>
  <n-grid :cols="2" :x-gap="12">
    <n-grid-item>
      <n-form-item label="Simple Task Provider" label-placement="top">
        <gaia-select
          :value="settings.simple_task_provider ?? null"
          :options="providerOptions"
          :loading="loadingProviders"
          clearable
          placeholder="Backend default"
          @update:value="onSimpleProviderChange"
        />
      </n-form-item>
    </n-grid-item>
    <n-grid-item>
      <n-form-item label="Simple Task Model" label-placement="top">
        <gaia-select
          :value="settings.simple_task_model ?? null"
          :options="simpleModelOptions"
          :disabled="!settings.simple_task_provider"
          clearable
          filterable
          placeholder="Backend default"
          @update:value="onSimpleModelChange"
        />
      </n-form-item>
    </n-grid-item>
    <n-grid-item>
      <n-form-item label="Complex Task Provider" label-placement="top">
        <gaia-select
          :value="settings.complex_task_provider ?? null"
          :options="providerOptions"
          :loading="loadingProviders"
          clearable
          placeholder="Backend default"
          @update:value="onComplexProviderChange"
        />
      </n-form-item>
    </n-grid-item>
    <n-grid-item>
      <n-form-item label="Complex Task Model" label-placement="top">
        <gaia-select
          :value="settings.complex_task_model ?? null"
          :options="complexModelOptions"
          :disabled="!settings.complex_task_provider"
          clearable
          filterable
          placeholder="Backend default"
          @update:value="onComplexModelChange"
        />
      </n-form-item>
    </n-grid-item>
  </n-grid>
</template>
